import { useDismiss, useFloating, useInteractions } from '@floating-ui/react'
import { Link } from '@tanstack/react-router'
import { type FC, useCallback, useState } from 'react'

const MenuDropdownItem: FC<{
  linkUrl: string
  title: string
  onClick?: () => void
}> = ({ linkUrl, title, onClick }) => (
  <Link to={linkUrl}>
    <button
      onClick={onClick}
      className="cursor-pointer p-2 hover:bg-serblue-dark hover:text-[#D3D027] block w-full text-start"
    >
      <span className="h-6">{title}</span>
    </button>
  </Link>
)

interface MenuDropdownProps {
  title: string
  items: Array<{
    url: string
    title: string
  }>
}

export const MenuDropdown: FC<MenuDropdownProps> = ({ items, title }) => {
  const [open, setOpen] = useState(false)
  const { x, y, refs, strategy, context } = useFloating({
    placement: 'bottom-end',
    open,
    onOpenChange: setOpen,
  })

  const dismiss = useDismiss(context)
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss])
  const toggle = useCallback(() => {
    setOpen((isOpen) => !isOpen)
  }, [setOpen])

  return (
    <>
      <button
        ref={refs.setReference}
        {...getReferenceProps()}
        onClick={toggle}
        id="infoDropdownButton"
        className="inline-flex items-center text-center p-2 hover:bg-serblue-dark hover:text-[#D3D027]"
      >
        <span className="h-6 inline-flex items-center">
          {title}
          <svg
            className="ml-2 h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 9l-7 7-7-7"
            ></path>
          </svg>
        </span>
      </button>
      {open && (
        <span
          ref={refs.setFloating}
          style={{
            position: strategy,
            top: y ?? '',
            left: x ?? '',
          }}
          {...getFloatingProps()}
        >
          <ul className="bg-serblue-light" aria-labelledby="infoDropdownButton">
            {items.map(({ title, url }) => (
              <MenuDropdownItem
                onClick={() => {
                  setOpen(false)
                }}
                key={`item-${title}`}
                linkUrl={url}
                title={title}
              />
            ))}
          </ul>
        </span>
      )}
    </>
  )
}
